import { useQuery } from '@tanstack/react-query';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { AccountingSeat } from 'axiosApi/models/accounting-seat';
import { queryKeys } from '../queryRegistry';
import { useContext } from 'react';
import { AuthContext } from 'components/authentication/context/authContext';

/**
 * Transformed movement interface with proper amount handling
 */
export interface LedgerMovement {
  id: number;
  date: Date;
  description: string;
  amount: number;        // Original amount (always positive from DB)
  displayAmount: number; // Same as amount - UI uses colors for debit/credit distinction
  incoming: number;      // 0 = outgoing (debit), 1 = incoming (credit)
  currency: {
    id: number;
    code: string;
    name?: string;
  } | null; // Nullable currency - let UI handle missing data
  type: 'credit' | 'debit';
  accountId: number;
  movementType: string | null;
  idRelatedAccount: number | null;
  idRelatedAccountingSeat: number | null;
  exchangeRate: number | null;
  relatedAccount: {
    id: number;
    accountNumber: string;
    accountName: string;
    currency: string;
  } | null;
}

/**
 * Pagination and sorting parameters for movements
 */
export interface MovementsParams {
  pageNum?: number;
  pageSize?: number;
  sort?: string;
  order?: 'asc' | 'desc';
  startDate?: string; // yyyy-MM-dd
  endDate?: string;   // yyyy-MM-dd
}

/**
 * Paginated movements response
 */
export interface PaginatedMovements {
  data: LedgerMovement[];
  totalRecords: number;
  pageNum: number;
  pageSize: number;
}

/**
 * Transform AccountingSeat to LedgerMovement with proper amount signs
 */
const transformMovement = (seat: AccountingSeat): LedgerMovement => {
  const baseAmount = (seat.amount || 0) / 100; 
  const isCredit = (seat.incoming || 0) === 1;
  
  return {
    id: seat.id || 0,
    date: seat.date ? new Date(seat.date) : new Date(0), 
    description: seat.description || '',
    amount: baseAmount,
    displayAmount: baseAmount,
    incoming: seat.incoming || 0,
    currency: seat.currency ? {
      id: seat.currency.id,
      code: seat.currency.code,
      name: seat.currency.name,
    } : null, // No defaults - let UI handle missing currency
    type: isCredit ? 'credit' : 'debit',
    accountId: seat.idAccount || 0,
    movementType: seat.movementType ?? null,
    idRelatedAccount: seat.idRelatedAccount ?? null,
    idRelatedAccountingSeat: seat.idRelatedAccountingSeat ?? null,
    exchangeRate: seat.exchangeRate ?? null,
    relatedAccount: seat.relatedAccount ? {
      id: seat.relatedAccount.id ?? 0,
      accountNumber: seat.relatedAccount.accountNumber ?? '',
      accountName: seat.relatedAccount.name ?? '',
      currency: seat.relatedAccount.currency?.code ?? '',
    } : null,
  };
};

/**
 * Hook to fetch ledger movements for a specific account
 */
export const useLedgerMovements = (
  accountId: number, 
  params: MovementsParams = {},
  enabled = true
) => {
  const { invoiceApi }: Api = useApi();
  const { userEntityDetails } = useContext(AuthContext);
  const userEntityId = userEntityDetails?.id;

  const {
    pageNum = 1,
    pageSize = 25,
    sort = 'date',
    order = 'desc',
    startDate,
    endDate,
  } = params;

  return useQuery({
    queryKey: queryKeys.ledgerMovements(accountId, pageNum, pageSize, sort, order, startDate, endDate),
    queryFn: async (): Promise<PaginatedMovements> => {
      // Build filter array
      const filters: string[] = [`idaccount:${accountId}`];
      if (startDate) filters.push(`startdate:${startDate}`);
      if (endDate)   filters.push(`enddate:${endDate}`);

      // Call ledger search endpoint with account filter
      const response = await invoiceApi.apiVversionLedgerSearchGet(
        '1', // version
        pageNum,
        pageSize,
        '', 
        sort,
        order,
        filters
      );

      const searchData = response.data.data;
      const movements = searchData?.queryResult || [];
      
      // Transform movements with proper amount signs
      const transformedMovements = movements.map(transformMovement);

      return {
        data: transformedMovements,
        totalRecords: searchData?.totalRecords || 0,
        pageNum,
        pageSize,
      };
    },
    enabled: enabled && !!accountId && !!userEntityId,
    staleTime: 0,
    placeholderData: (prev) => prev,
  });
};

/**
 * Hook to get movements summary for an account
 */
export const useMovementsSummary = (accountId: number, enabled = true) => {
  const { data: movements, isLoading } = useLedgerMovements(
    accountId, 
    { pageNum: 1, pageSize: 1000 }, // Get all movements for summary
    enabled
  );

  const summary = {
    totalMovements: movements?.totalRecords || 0,
    totalCredits: movements?.data.filter(m => m.type === 'credit').length || 0,
    totalDebits: movements?.data.filter(m => m.type === 'debit').length || 0,
    totalCreditAmount: movements?.data
      .filter(m => m.type === 'credit')
      .reduce((sum, m) => sum + m.amount, 0) || 0,
    totalDebitAmount: movements?.data
      .filter(m => m.type === 'debit')
      .reduce((sum, m) => sum + m.amount, 0) || 0,
  };

  return {
    summary,
    isLoading,
  };
};